'use client';

import { useEffect, useRef } from 'react';

interface VideoPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  autoPlay?: boolean;
}

export default function VideoPlayer({ autoPlay = true, ...props }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const tryPlay = async () => {
      try {
        await video.play();
      } catch (err) {
        console.warn('Autoplay failed:', err);
      }
    };

    // Pause video when tab is hidden to save CPU
    const onVisibilityChange = () => {
      if (!video) {
        return;
      }

      if (document.visibilityState === 'hidden') {
        video.pause();
      } else if (document.visibilityState === 'visible' && autoPlay) {
        tryPlay();
      }
    };

    // Use Intersection Observer to pause videos when not visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!video) {
            return;
          }

          if (entry.isIntersecting && autoPlay) {
            tryPlay();
          } else {
            // Pause when not visible to save CPU
            video.pause();
          }
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(video);
    document.addEventListener('visibilitychange', onVisibilityChange);

    if (autoPlay) {
      tryPlay();
    }

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (video) {
        video.pause();
      }
    };
  }, [autoPlay]);

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      autoPlay={autoPlay}
      controls={false}
      suppressHydrationWarning
      {...props}
    />
  );
}
